import { useTranslation } from 'react-i18next'

import {
  type UpdateMovePayload,
  useCurrentMove,
  useSelectedQuote,
  useUpdateMoveAction,
} from '../../../modules/moves'
import {
  Modal,
  ModalDescription,
  ModalHeader,
  ModalTitle,
} from '../../../components/Modal'
import AddressesForm from '../../../components/forms/editmove/AddressesForm'
import { useErrorFormatter } from '../../../components/errors/useErrorFormatter'
import { errorNotification } from '../../../components/ToastNotifications'
import { type MoveLocation } from '../../../modules/moves/core/models/subModels/moveLocations.models'
import { getUsedLocations } from '../../../modules/moves/core/locations.utils'

const ValidateAddressesModal: React.FC = () => {
  const { t } = useTranslation()
  const move = useCurrentMove()
  const selectedQuote = useSelectedQuote()
  const [updateMove] = useUpdateMoveAction()
  const formatError = useErrorFormatter()

  const onSubmit = async (data: UpdateMovePayload) => {
    try {
      await updateMove(data)
    } catch (error) {
      errorNotification(formatError(error))
    }
  }

  const isAddressComplete = (location?: MoveLocation) => {
    if (!location) {
      return true
    }
    if (location.elevatorReservationRequired === null) {
      return false
    }

    if (!('country' in location.address)) {
      return false
    }
    const hasStreet = !!location.address.street
    const hasCity = !!location.address.city
    const hasRegion = !!location.address.region
    const hasCountry = !!location.address.country
    const hasPostalCode = !!location.address.postalCode

    return hasStreet && hasCity && hasRegion && hasCountry && hasPostalCode
  }

  const locations = getUsedLocations(move)
  const hasIncompleteAddresses = !!locations.find((location) => !isAddressComplete(location))
  const hasStops = locations.length > 2
  const open = hasIncompleteAddresses && !selectedQuote?.customized

  return (
    <Modal
      open={open}
      style={{ maxWidth: hasStops ? 850 : 1200 }}
      showCloseIcon={false}
    >
      <ModalHeader>
        <ModalTitle>
          { t('pages.checkout.validateAddressModal.title') }
        </ModalTitle>
      </ModalHeader>

      <ModalDescription>
        <AddressesForm
          onSubmit={onSubmit}
          wide={!hasStops}
        />
      </ModalDescription>
    </Modal>
  )
}

export default ValidateAddressesModal
